/**
 * Eine Übersicht der Seite „Bestellungen" (Produkt, Kanal, Blog oder WKZ): Tabelle mit Summenzeile AUS DEM BACKEND und
 * Excel-Export genau dieser Zeilen. Spalten in vier Gruppen — Bestellungen (inkl. verweigerter, GF-Wunsch 27.10.2026),
 * Anfos, Bisher bezahlt (mit bezahlten Anfos und Ø Alter, Regel der Sektion) und Erwarteter Wert. Karten nie zugeklappt.
 * Kurze Köpfe und Einheiten dieser Tabelle gelten auch für die Karte „Verweigerte Bestellungen" (`BESTELL_KOEPFE`).
 * Persona-Umbau A5: „Bisher bezahlt" ist nur Standardspalte, wenn die Anfos des Zeitraums im Schnitt mindestens
 * `BEZAHLT_AB_ALTER_TAGE` alt sind; sonst steht sie in der Spaltenwahl (eine Wahl für alle vier Übersichten).
 */
import { FC, ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Link, useMediaQuery, useTheme } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ChartCard from '../../../components/auswertungen/ChartCard';
import SortableTable, { Spalte, spaltenMeta } from '../../../components/auswertungen/SortableTable';
import SpaltenMenue from '../../../components/auswertungen/SpaltenMenue';
import { useSpaltenwahl } from '../../../components/auswertungen/spaltenwahl';
import { BezahltBuendel, Hinweis, Laden, int, useBreitePx } from '../../../components/auswertungen/kennzahlTeile';
import { exportToExcel } from '../../../helpers/exportFuncs';
import { BestellDimension, BestellKennzahlen, BestellungenAntwort, BestellZeile } from '../../../helpers/bestellungenData';
import { formatEur, formatPct, TERMS } from '../../../helpers/kpiDefinitions';

const prozent = (v: number | null | undefined): string => (v === null || v === undefined ? '—' : formatPct(v, 1));
const euro = (v: number | null | undefined): string => (v === null || v === undefined ? '—' : formatEur(v, 0));
const alter = (v: number | null | undefined): number | null => (v === null || v === undefined ? null : v);

/**
 * D5 (Audit 30.10.2026): „Anteil verweigert" grau (und beim Sortieren hinten) unter so vielen Bestellungen — dieselbe
 * Grau-Schwelle wie die übrigen Anteile der Sektion (`GRAU_SCHWELLEN.anteil_anfos` im Backend, 20).
 */
export const VERWEIGERT_ANTEIL_MIN_BESTELLUNGEN = 20;
export const verweigertAnteilGrau = (bestellungen: number): string | null =>
  bestellungen < VERWEIGERT_ANTEIL_MIN_BESTELLUNGEN ? `unter ${VERWEIGERT_ANTEIL_MIN_BESTELLUNGEN} Bestellungen — der Anteil kann am Zufall hängen` : null;

/** D16: am Handy (Kartenbreite unter 640 px) nur Name, Anfos und Erwarteter Wert; die übrigen über „Spalten". */
export const UEBERSICHT_SCHMAL = ['name', 'anfos', 'value_sum'] as const;

/** Ab diesem Ø Alter der Anfos (Tage) ist „Bisher bezahlt" Standardspalte (Plan A5); darunter ist es fast 0. */
export const BEZAHLT_AB_ALTER_TAGE = 60;

/** Standardspalten der Übersichten: alle — „Bisher bezahlt" nur bei genug altem Zeitraum. */
export const uebersichtStandard = (alleKeys: readonly string[], avgAge: number | null): string[] =>
  alleKeys.filter((k) => k !== 'paid' || (avgAge !== null && avgAge >= BEZAHLT_AB_ALTER_TAGE));

/**
 * Köpfe der Bestellungen-Spalten — dieselben in den Übersichten und in der Karte „Verweigerte Bestellungen"
 * (P5-Review #24: dort hießen sie „Davon verweigert", „Anteil verweigert (an Bestellungen)" + Einheit doppelt).
 */
export const BESTELL_KOEPFE = {
  bestellungen: { label: TERMS.bestellungenAlle.short, einheit: 'inkl. verweigerte', tip: TERMS.bestellungenAlle.tip },
  verweigert: { label: TERMS.verweigert.short, einheit: undefined as string | undefined, tip: TERMS.verweigert.tip },
  anteil: { label: TERMS.verweigert.short, einheit: '% an Bestellungen', tip: TERMS.verweigertAnteil.tip },
  anfos: { label: TERMS.anfos.label, einheit: undefined as string | undefined, tip: TERMS.anfos.tip },
};

/** Zellen einer Kennzahl-Zeile — für Datenzeilen und Summenzeile dieselbe Darstellung. */
const zellen = (k: BestellKennzahlen) => ({
  bestellungen: int(k.bestellungen),
  verweigert: int(k.verweigert),
  verweigert_anteil: prozent(k.verweigert_anteil),
  // Vorläufige als Unterzeile der Anfos (eigene Spalte liess die Tabelle bei 1.280 px mit offener Leiste überlaufen).
  anfos: (
    <Box sx={{ lineHeight: 1.25 }}>
      {int(k.anfos)}
      {k.provisional > 0 && (
        <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary', whiteSpace: 'nowrap' }}>
          davon {int(k.provisional)} vorläufig
        </Box>
      )}
    </Box>
  ),
  share: prozent(k.share),
  paid: <BezahltBuendel wert={euro(k.paid_revenue)} zahler={k.payers} alterTage={alter(k.avg_age_days)} />,
  value_sum: euro(k.value_sum),
  value_per_anfo: euro(k.value_per_anfo),
});

/** Spalten mit Zelle, Sortierwert und Summe aus der Gesamtzeile. */
function spalten(
  nameLabel: string,
  nameTip: string,
  total: BestellKennzahlen | null,
  schmal: boolean,
): Spalte<BestellZeile>[] {
  const sum = total ? zellen(total) : null;
  const zahl = (
    key: keyof ReturnType<typeof zellen>,
    label: string,
    tip: string,
    sortValue: (r: BestellZeile) => number | null,
    kopfGruppe: string,
    einheit?: string,
    hinweis?: string,
    unsicher?: (r: BestellZeile) => string | null,
  ): Spalte<BestellZeile> => ({
    key,
    label,
    tip,
    einheit,
    hinweis,
    unsicher,
    align: 'right',
    kopfGruppe,
    gruppe: kopfGruppe,
    render: (r) => zellen(r)[key],
    sortValue,
    summary: sum ? <Box sx={{ fontWeight: 700 }}>{sum[key]}</Box> : undefined,
  });
  const alle: Spalte<BestellZeile>[] = [
    {
      key: 'name',
      label: nameLabel,
      tip: nameTip,
      pflicht: true,
      gruppe: 'Name',
      minWidth: schmal ? 110 : 150,
      render: (r) => (
        <Box sx={{ lineHeight: 1.3, whiteSpace: 'normal' }}>
          <Box component="span" sx={{ fontWeight: r.rest ? 400 : 600 }}>
            {r.label}
          </Box>
          {r.detail && r.detail !== r.label && (
            <Box component="span" sx={{ display: 'block', fontSize: 12, color: 'text.secondary' }}>
              {r.detail}
            </Box>
          )}
        </Box>
      ),
      sortValue: (r) => r.label,
    },
    // Kurze Köpfe mit Einheit als zweiter Zeile (sonst lief die Tabelle bei 1.280 px mit offener Leiste über); der volle
    // Begriff steht im Tooltip. „Anteil verweigert" nennt seinen Nenner in der Einheit („an Bestellungen").
    zahl('bestellungen', BESTELL_KOEPFE.bestellungen.label, BESTELL_KOEPFE.bestellungen.tip, (r) => r.bestellungen, 'Bestellungen', BESTELL_KOEPFE.bestellungen.einheit),
    zahl('verweigert', BESTELL_KOEPFE.verweigert.label, BESTELL_KOEPFE.verweigert.tip, (r) => r.verweigert, 'Bestellungen'),
    zahl(
      'verweigert_anteil',
      BESTELL_KOEPFE.anteil.label,
      BESTELL_KOEPFE.anteil.tip,
      (r) => r.verweigert_anteil,
      'Bestellungen',
      BESTELL_KOEPFE.anteil.einheit,
      `Grau unter ${VERWEIGERT_ANTEIL_MIN_BESTELLUNGEN} Bestellungen.`,
      (r) => verweigertAnteilGrau(r.bestellungen),
    ),
    zahl('anfos', TERMS.anfos.label, `${TERMS.anfos.tip} Darunter, falls vorhanden: ${TERMS.vorlaeufig.tip}`, (r) => r.anfos, 'Anfos'),
    zahl('share', 'Anteil', TERMS.anteilAnAllenAnfos.tip, (r) => r.share, 'Anfos', '% an Anfos'),
    zahl(
      'paid',
      TERMS.umsatzNetto.label,
      `${TERMS.umsatzNetto.tip} Darunter: ${TERMS.zahlerBisher.tip} ${TERMS.alterAnfos.tip}`,
      (r) => r.paid_revenue,
      'Echtes Geld',
      'netto',
      TERMS.bezahltSpalteAus.tip,
    ),
    // Gruppenkopf kurz (P5-Review #25: „Erwarteter Wert (bei Bestellung)" brach Wort für Wort um); „bei Bestellung" steht
    // im Tooltip. #13: warum „Bisher bezahlt" beim Jahresbezug über dem Erwarteten Wert liegen kann.
    zahl('value_sum', 'Summe', TERMS.wertSumme.tip, (r) => r.value_sum, TERMS.wertBeiBestellung.short, 'netto', TERMS.wertUnterBezahlt.tip),
    zahl('value_per_anfo', 'Ø je Anfo', TERMS.wertJeAnfoP5.tip, (r) => r.value_per_anfo, TERMS.wertBeiBestellung.short, 'netto', TERMS.wertUnterBezahlt.tip),
  ];
  return alle;
}

/** Excel = die Tabelle: dieselben Zeilen und Kennzahlen, Summenzeile aus dem Backend am Ende. */
export function excelZeilen(data: BestellungenAntwort, dimension: BestellDimension, nameLabel: string) {
  const u = data.uebersichten[dimension];
  const zeile = (name: string, detail: string | null, k: BestellKennzahlen) => ({
    [nameLabel]: name,
    Detail: detail,
    [TERMS.bestellungenAlle.label]: k.bestellungen,
    [TERMS.verweigert.label]: k.verweigert,
    [`${TERMS.verweigertAnteil.label} (%)`]: k.verweigert_anteil === null ? null : Math.round(k.verweigert_anteil * 10) / 10,
    [TERMS.anfos.label]: k.anfos,
    [`${TERMS.anteilAnAllenAnfos.label} (%)`]: k.share === null ? null : Math.round(k.share * 10) / 10,
    'Davon vorläufig': k.provisional,
    [TERMS.zahlerBisher.label]: k.payers,
    [`${TERMS.umsatzNetto.label} (netto, €)`]: k.paid_revenue,
    [`${TERMS.alterAnfos.label} (Tage)`]: k.avg_age_days === null ? null : Math.round(k.avg_age_days),
    [`${TERMS.wertSumme.label} (netto, €)`]: k.value_sum,
    [`${TERMS.wertJeAnfoP5.label} (netto, €)`]: k.value_per_anfo,
  });
  return [...u.rows.map((r) => zeile(r.label, r.detail, r)), zeile('Gesamt', null, u.total)];
}

/** Lesehinweis der Blog-Übersicht — derselbe Inhalt wie die Blog-Karte im Kapitel Kanäle (P3). */
export const BlogHinweis: FC = () => (
  <>
    <Box component="span" sx={{ fontWeight: 600 }}>
      Über welche Marke die Bestellung lief.
    </Box>{' '}
    Webinar-Käufe stehen meist bei Finanztrends (Kennung des Webinarraums). Kam die Einladung über einen Heat-Up einer Schwestermarke,
    trägt der Kauf seit Dezember 2025 oft deren Kennung und steht dann bei dieser Marke. Welche Einladung jemanden zum Webinar
    gebracht hat, zeigt die Karte „Webinar-Einladungen“ im Kapitel{' '}
    <Link component={RouterLink} to="/auswertungen/kanaele" underline="hover">
      Kanäle &amp; Werbeflächen
    </Link>
    .
  </>
);

interface Props {
  dimension: BestellDimension;
  titel: string;
  nameLabel: string;
  nameTip: string;
  howToRead: ReactNode;
  zeitraumText: string;
  data: BestellungenAntwort | null;
  fehler: string | null;
  dateiBasis: string;
}

const UebersichtKarte: FC<Props> = ({ dimension, titel, nameLabel, nameTip, howToRead, zeitraumText, data, fehler, dateiBasis }) => {
  const theme = useTheme();
  const schmal = useMediaQuery(theme.breakpoints.down('sm'));
  // Unter 1.000 px Kartenbreite enger setzen (bei 1.280 px Fenster mit offener Leiste ist die Karte ~910 px breit).
  const u = data?.uebersichten[dimension] ?? null;
  const alleCols = spalten(nameLabel, nameTip, u?.total ?? null, schmal);
  // Menü: „verweigert" stand zweimal (Anzahl und Anteil) — der Anteil heißt dort mit Einheit.
  const meta = spaltenMeta(alleCols).map((m) => (m.key === 'verweigert_anteil' ? { ...m, label: TERMS.verweigertAnteil.short } : m));
  const standard = uebersichtStandard(
    alleCols.map((c) => c.key),
    u?.total.avg_age_days ?? null,
  );
  // Ein Speicherschlüssel für alle vier Übersichten (gleiche Spalten): „Bisher bezahlt" an einer Karte gilt für alle.
  const schmalStandard = standard.filter((k) => (UEBERSICHT_SCHMAL as readonly string[]).includes(k));
  const wahl = useSpaltenwahl('bestellungenUebersicht', meta, { schmal: schmalStandard, mittel: standard, breit: standard });
  const sichtbar = new Set(wahl.sichtbar);
  const cols = alleCols.filter((c) => sichtbar.has(c.key));
  const bezahltAus = !sichtbar.has('paid') && u !== null && !standard.includes('paid');
  const [breiteRef, breitePx] = useBreitePx(wahl.messRef);
  const eng = schmal || (breitePx !== null && breitePx < 1000);
  return (
    <ChartCard
      title={titel}
      subtitle={zeitraumText}
      howToRead={howToRead}
      actions={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <SpaltenMenue spalten={meta} wahl={wahl} />
        <Button
          size="small"
          startIcon={<FileDownloadOutlinedIcon />}
          disabled={!data}
          onClick={() => data && exportToExcel(excelZeilen(data, dimension, nameLabel), `${dateiBasis}_${dimension}`)}
          sx={{ color: 'text.secondary' }}
        >
          Excel
        </Button>
        </Box>
      }
      terms={[
        { label: TERMS.bestellungenAlle.label, tip: TERMS.bestellungenAlle.tip },
        { label: TERMS.verweigertAnteil.label, tip: TERMS.verweigertAnteil.tip },
        { label: TERMS.anfos.label, tip: TERMS.anfos.tip },
        { label: TERMS.umsatzNetto.label, tip: TERMS.umsatzNetto.tip },
        { label: TERMS.wertBeiBestellung.label, tip: TERMS.wertBeiBestellung.tip },
        { label: TERMS.vorlaeufig.label, tip: TERMS.vorlaeufig.tip },
      ]}
      source="PL-X-Bestellungen und Zahlungen, Anfo-Werte"
      freshnessKey={['plx_orders', 'anfo_werte']}
    >
      <Box ref={breiteRef}>
      {!u ? (
        <Laden fehler={fehler} />
      ) : (
        <>
          <SortableTable
            rows={u.rows}
            columns={cols}
            rowKey={(r) => r.key || '∅'}
            initialSort={{ key: 'anfos', dir: 'desc' }}
            abschnitt={(r) => (r.rest ? '' : null)}
            summaryLabel="Gesamt"
            eng={eng}
            festeErsteSpalte
            maxHeight={null}
          />
          <Hinweis>
            Euro netto. Bestellungen = {TERMS.anfos.label} + verweigerte; alle Quoten außer „Anteil verweigert“ rechnen mit den Anfos.
            Vorläufig ergänzte Anfos zählen als Anfo, aber nicht in bezahlten Anfos, „bezahlt“ und Erwartetem Wert.
            {u.total.anfos === 0 && u.total.bestellungen === 0 ? ' Im Zeitraum gibt es keine Bestellung.' : ''}
            {bezahltAus &&
              ` „${TERMS.umsatzNetto.label}“ ist ausgeblendet: die Anfos sind im Schnitt erst ${int(u.total.avg_age_days === null ? null : Math.round(u.total.avg_age_days))} Tage alt (unter ${BEZAHLT_AB_ALTER_TAGE}) — bezahlt ist dann fast nichts. Über „Spalten“ einblendbar; der Excel-Export enthält die Spalte immer.`}
          </Hinweis>
        </>
      )}
      </Box>
    </ChartCard>
  );
};

export default UebersichtKarte;
